import React from 'react';
import { cn } from '../../../lib/utils';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../components/ui/table';
import { Edit, Trash2, Loader2, CheckCircle2, XCircle, Tag, Calendar, Users } from 'lucide-react';
import { PromoCodesTableProps } from '../types';

export function PromoCodesTable({
  isDark,
  promoCodes,
  isLoading,
  onEdit,
  onDelete,
  formatDate,
  formatValue,
}: PromoCodesTableProps) {
  const isExpired = (validUntil: string | null) => {
    if (!validUntil) return false;
    return new Date(validUntil) < new Date();
  };

  const isNotYetValid = (validFrom: string | null) => {
    if (!validFrom) return false;
    return new Date(validFrom) > new Date();
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-[#C9A227]" />
      </div>
    );
  }

  return (
    <Table>
      <TableHeader>
        <TableRow className={cn(
          isDark ? 'border-slate-700 hover:bg-slate-800/50' : 'border-slate-100 hover:bg-slate-50'
        )}>
          <TableHead className={cn('font-semibold', isDark ? 'text-slate-400' : 'text-slate-500')}>Код</TableHead>
          <TableHead className={cn('font-semibold', isDark ? 'text-slate-400' : 'text-slate-500')}>Тип</TableHead>
          <TableHead className={cn('font-semibold', isDark ? 'text-slate-400' : 'text-slate-500')}>Значение</TableHead>
          <TableHead className={cn('font-semibold hidden lg:table-cell', isDark ? 'text-slate-400' : 'text-slate-500')}>Использовано</TableHead>
          <TableHead className={cn('font-semibold hidden md:table-cell', isDark ? 'text-slate-400' : 'text-slate-500')}>Период</TableHead>
          <TableHead className={cn('font-semibold', isDark ? 'text-slate-400' : 'text-slate-500')}>Статус</TableHead>
          <TableHead className={cn('font-semibold', isDark ? 'text-slate-400' : 'text-slate-500')}>Действия</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {promoCodes.length === 0 ? (
          <TableRow>
            <TableCell colSpan={7} className="text-center py-12 text-slate-500">
              Промокоды не найдены
            </TableCell>
          </TableRow>
        ) : (
          promoCodes.map((promoCode) => {
            const expired = isExpired(promoCode.valid_until);
            const notYetValid = isNotYetValid(promoCode.valid_from);
            const usageLimitReached = promoCode.max_uses !== null && promoCode.used_count >= promoCode.max_uses;
            const isActive = promoCode.is_active && !expired && !notYetValid && !usageLimitReached;

            return (
              <TableRow 
                key={promoCode.id}
                className={cn(
                  'transition-colors',
                  isDark 
                    ? 'border-slate-700 hover:bg-slate-800/50' 
                    : 'border-slate-100 hover:bg-slate-50'
                )}
              >
                <TableCell>
                  <div className="flex items-center gap-2">
                    <Tag className={cn('w-4 h-4', isDark ? 'text-slate-400' : 'text-slate-500')} />
                    <span className={cn('font-mono text-sm font-semibold', isDark ? 'text-white' : 'text-[#111111]')}>
                      {promoCode.code}
                    </span>
                  </div>
                </TableCell>
                <TableCell>
                  <Badge className={cn(
                    'rounded-lg font-medium border-0',
                    promoCode.type === 'percent' ? 'bg-blue-500/10 text-blue-500' : 'bg-purple-500/10 text-purple-500'
                  )}>
                    {promoCode.type === 'percent' ? 'Процент' : 'Фикс. сумма'}
                  </Badge>
                </TableCell>
                <TableCell className={cn('font-medium', isDark ? 'text-white' : 'text-[#111111]')}>
                  {formatValue(promoCode.type, promoCode.value)}
                </TableCell>
                <TableCell className={cn('hidden lg:table-cell', isDark ? 'text-slate-300' : 'text-slate-700')}>
                  <div className="flex items-center gap-1">
                    <Users className="w-4 h-4" />
                    <span>{promoCode.used_count}{promoCode.max_uses !== null ? ` / ${promoCode.max_uses}` : ' / ∞'}</span>
                  </div>
                </TableCell>
                <TableCell className={cn('hidden md:table-cell text-xs', isDark ? 'text-slate-400' : 'text-slate-500')}>
                  <div className="flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    <span>{formatDate(promoCode.valid_from)} - {formatDate(promoCode.valid_until)}</span>
                  </div>
                </TableCell>
                <TableCell>
                  <Badge className={cn(
                    'rounded-lg font-medium border-0',
                    isActive ? 'bg-emerald-500/10 text-emerald-500'
                      : expired ? 'bg-red-500/10 text-red-500'
                      : notYetValid ? 'bg-amber-500/10 text-amber-500'
                      : 'bg-slate-500/10 text-slate-500'
                  )}>
                    {isActive ? <><CheckCircle2 className="w-3 h-3 mr-1" />Активен</>
                      : expired ? <><XCircle className="w-3 h-3 mr-1" />Истёк</>
                      : notYetValid ? <><Calendar className="w-3 h-3 mr-1" />Ещё не начат</>
                      : usageLimitReached ? <><XCircle className="w-3 h-3 mr-1" />Лимит исчерпан</>
                      : <><XCircle className="w-3 h-3 mr-1" />Неактивен</>
                    }
                  </Badge>
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <Button variant="ghost" size="sm" aria-label="Редактировать промокод" onClick={() => onEdit(promoCode)}
                      className={cn('h-8 px-2 rounded-lg', isDark ? 'text-slate-400 hover:bg-slate-800 hover:text-white' : 'text-slate-600 hover:bg-slate-100')}>
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="sm" aria-label="Удалить промокод" onClick={() => onDelete(promoCode.id)}
                      className={cn('h-8 px-2 rounded-lg', isDark ? 'text-slate-400 hover:bg-slate-800 hover:text-red-400' : 'text-slate-600 hover:bg-slate-100 hover:text-red-600')}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            );
          })
        )}
      </TableBody>
    </Table>
  );
}
